import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import API from "../../../../services/api";
import {
  Typography,
  Spin,
  Button,
  message,
  Empty,
  Tag,
  Space,
  Popconfirm,
  Pagination,
  Select,
  Modal,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  EnvironmentOutlined,
  ShopOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import "./MyBusinesses.css";

const { Text, Paragraph } = Typography;

function MyBusinesses() {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [fetching, setFetching] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [filter, setFilter] = useState("all");
  const [stats, setStats] = useState({
    total: 0,
    activeCount: 0,
    pendingCount: 0,
  });
  const [isMobileFilter, setIsMobileFilter] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 460 : false
  );
  const navigate = useNavigate();

  const fetchMyBusinesses = useCallback(
    async (pageNumber = 1) => {
      if (fetching) return;

      try {
        setFetching(true);
        setLoading(true);

        const res = await API.get(`/businesses/my/listings?page=${pageNumber}&filter=${filter}`);

        setBusinesses(res.data.businesses || []);
        setTotalPages(res.data.totalPages || 1);
        setTotal(res.data.total || 0);
        setStats(res.data.stats || {});
      } catch (err) {
        console.error("Fetch error:", err);
        message.error("Failed to load listings");
        setBusinesses([]);
      } finally {
        setLoading(false);
        setFetching(false);
      }
    }, [fetching, filter]);

  useEffect(() => {
    setPage(1);
    fetchMyBusinesses(1);
  }, [filter]);

  useEffect(() => {
    fetchMyBusinesses(page);
  }, [page]);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobileFilter(window.innerWidth <= 460);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleStatusToggle = async (id, currentStatus) => {
    try {
      setActionLoadingId(id);
      const endpoint = currentStatus === "active" ? "/deactivate" : "/activate";
      await API.put(`/businesses/${id}${endpoint}`);
      message.success(currentStatus === "active" ? "Deactivated" : "Activated");
      fetchMyBusinesses(page);
    } catch (err) {
      message.error("Status update failed");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDelete = (id, title) => {
    Modal.confirm({
      title: "Delete Listing",
      content: `Are you sure you want to delete "${title}"? This action cannot be undone.`,
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await API.delete(`/businesses/${id}`);
          message.success("Listing deleted successfully");
          setBusinesses((prev) => prev.filter((b) => b._id !== id));
        } catch (err) {
          message.error("Failed to delete listing");
        }
      },
    });
  };

  const getStatusConfig = (status) =>
    ({
      active: { color: "#10b981", bg: "#ecfdf5", label: "Active" },
      inactive: { color: "#6b7280", bg: "#f3f4f6", label: "Inactive" },
    })[status] || { color: "#ef4444", bg: "#fef2f2", label: "Error" };

  const getApprovalConfig = (status) =>
    ({
      approved: { color: "#3b82f6", bg: "#eff6ff", label: "Approved" },
      pending: { color: "#f59e0b", bg: "#fef3c7", label: "Pending" },
      rejected: { color: "#ef4444", bg: "#fef2f2", label: "Rejected" },
    })[status] || { color: "#6b7280", bg: "#f3f4f6", label: "Unknown" };

  if (loading) {
    return (
      <div className="biz-page">
        <div className="biz-shell">
          <div className="biz-loading">
            <Spin size="large" />
          </div>
        </div>
      </div>
    );
  }

  const activeCount = stats.activeCount;
  const pendingCount = stats.pendingCount;
  const totalBusinesses = stats.total;

  const filteredBusinesses = businesses;

  const filterOptions = [
    { label: "All", value: "all" },
    { label: "Approved", value: "approved" },
    { label: "Rejected", value: "rejected" },
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
  ];

  return (
    <div className="biz-page">
      <div className="biz-shell">
        {/* ── OVERVIEW ── */}
        <section className="biz-overview">
          <div className="biz-overview__stats">
            <div className="biz-overview__stat">
              <div className="biz-overview__value">{totalBusinesses}</div>
              <div className="biz-overview__label">Total Listings</div>
            </div>

            <div className="biz-overview__stat">
              <div className="biz-overview__value biz-overview__value--active">
                {activeCount}
              </div>
              <div className="biz-overview__label">Active</div>
            </div>

            <div className="biz-overview__stat">
              <div className="biz-overview__value biz-overview__value--pending">
                {pendingCount}
              </div>
              <div className="biz-overview__label">Pending Review</div>
            </div>

            {/* 4th stat slot — kept as empty placeholder matching original */}
            <div className="biz-overview__stat" />
          </div>

          <Link to="/add-business" className="biz-overview__cta">
            <Button size="large" type="primary" icon={<PlusOutlined />}>
              Add New Business
            </Button>
          </Link>
        </section>

        {/* ── TOOLBAR ── */}
        <section className="biz-toolbar">
          <div className="biz-toolbar__left">
            <div className="biz-toolbar__title-wrap">
              <Text className="biz-toolbar__title">My Businesses</Text>
              <Text className="biz-toolbar__subtitle">
                Manage all your business listings in one place
              </Text>
            </div>
          </div>

          <div className="biz-toolbar__right">
            {isMobileFilter ? (
              <Select
                value={filter}
                onChange={setFilter}
                options={filterOptions}
                className="biz-filters__select"
                classNames={{ popup: { root: "biz-filters__dropdown" } }}
                size="middle"
              />
            ) : (
              <div className="biz-filters">
                <Space wrap size={[10, 10]}>
                  <Button
                    type={filter === "all" ? "primary" : "default"}
                    onClick={() => setFilter("all")}
                  >
                    All
                  </Button>
                  <Button
                    type={filter === "approved" ? "primary" : "default"}
                    onClick={() => setFilter("approved")}
                  >
                    Approved
                  </Button>
                  <Button
                    type={filter === "rejected" ? "primary" : "default"}
                    onClick={() => setFilter("rejected")}
                  >
                    Rejected
                  </Button>
                  <Button
                    type={filter === "active" ? "primary" : "default"}
                    onClick={() => setFilter("active")}
                  >
                    Active
                  </Button>
                  <Button
                    type={filter === "inactive" ? "primary" : "default"}
                    onClick={() => setFilter("inactive")}
                  >
                    Inactive
                  </Button>
                </Space>
              </div>
            )}
          </div>
        </section>

        {/* ── EMPTY ── */}
        {businesses.length === 0 ? (
          <div className="biz-empty">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div>
                  <Text strong className="biz-empty__title">
                    No businesses yet
                  </Text>
                  <Paragraph className="biz-empty__text">
                    Create your first business listing to get started
                  </Paragraph>
                </div>
              }
            >
              <Link to="/add-business">
                <Button type="primary" size="large">
                  Create New Business
                </Button>
              </Link>
            </Empty>
          </div>
        ) : (
          <section className="biz-grid">
            {filteredBusinesses.map((business) => (
              <article className="biz-card" key={business._id}>
                {/* ── Media ── */}
                <div className="biz-card__media">
                  <img
                    src={
                      business.logo
                        ? business.logo + "?v=" + business.updatedAt
                        : business.images?.[0]
                          ? business.images[0] + "?v=" + business.updatedAt
                          : "/no-image.png"
                    }
                    alt={business.companyName}
                    className="biz-card__image"
                  />

                  <div className="biz-card__badges">
                    <Tag
                      className="biz-card__badge"
                      style={{
                        background: getStatusConfig(business.status).bg,
                        color: getStatusConfig(business.status).color,
                        borderColor: "transparent",
                      }}
                    >
                      {getStatusConfig(business.status).label}
                    </Tag>
                    <Tag
                      className="biz-card__badge"
                      style={{
                        background: getApprovalConfig(business.approvalStatus).bg,
                        color: getApprovalConfig(business.approvalStatus).color,
                        borderColor: "transparent",
                      }}
                    >
                      {getApprovalConfig(business.approvalStatus).label}
                    </Tag>
                    {business.type && (
                      <Tag
                        className="biz-card__badge biz-card__badge--type"
                        style={{
                          background: "#f1f5f9",
                          color: "#475569",
                          borderColor: "transparent",
                        }}
                      >
                        {business.type.toUpperCase()}
                      </Tag>
                    )}
                    {business.isFeatured && (
                      <Tag
                        className="biz-card__badge biz-card__badge--featured"
                        style={{
                          background: "#fef3c7",
                          color: "#d97706",
                          borderColor: "transparent",
                        }}
                      >
                        Featured
                      </Tag>
                    )}
                    <Button
                      type="text"
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleDelete(business._id, business.companyName)}
                      className="biz-card__delete-btn"
                      danger
                    />
                  </div>
                </div>

                {/* ── Body ── */}
                <div className="biz-card__body">
                  <Text
                    className="biz-card__title"
                    title={business.companyName}
                  >
                    {business.companyName}
                  </Text>

                  <div className="biz-card__industry">
                    <Text className="biz-card__industry-text">
                      {business.industry || "N/A"}
                    </Text>
                  </div>

                  <div className="biz-card__meta">
                    <div className="biz-card__meta-item">
                      <EnvironmentOutlined className="biz-card__meta-icon" />
                      <span>{business.location?.city || "N/A"}</span>
                    </div>

                    <div className="biz-card__meta-item">
                      <ShopOutlined className="biz-card__meta-icon" />
                      <span>{business.location?.state || "N/A"}</span>
                    </div>
                  </div>

                  {/* ── Actions ── */}
                  <div className="biz-card__footer">
                    <div className="biz-card__actions">
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() =>
                          navigate(`/edit-business/${business._id}`)
                        }
                        className="biz-btn biz-btn--secondary"
                      >
                        Edit
                      </Button>

                      <Popconfirm
                        title={`Mark as ${business.status === "active" ? "Inactive" : "Active"}`}
                        description="This will update visibility immediately."
                        onConfirm={() =>
                          handleStatusToggle(business._id, business.status)
                        }
                        okText="Confirm"
                        cancelText="Cancel"
                        okButtonProps={{ danger: business.status === "active" }}
                      >
                        <Button
                          size="small"
                          loading={actionLoadingId === business._id}
                          className={`biz-btn ${business.status === "active"
                            ? "biz-btn--active"
                            : "biz-btn--inactive"
                            }`}
                        >
                          {business.status === "active"
                            ? "Deactivate"
                            : "Activate"}
                        </Button>
                      </Popconfirm>

                      <Link
                        to={`/business/${business.slug}`}
                        className="biz-card__view-link"
                      >
                        <Button
                          size="small"
                          icon={<EyeOutlined />}
                          className="biz-btn biz-btn--primary"
                        >
                          View
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </section>
        )}

        {/* ── PAGINATION ── */}
        {total > 0 && (
          <div className="biz-pagination">
            <Pagination
              current={page}
              total={total}
              pageSize={15}
              onChange={(p) => setPage(p)}
              showSizeChanger={false}
              responsive
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default MyBusinesses;